import { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Routes as RouterRoutes,
  Route,
  Navigate,
} from 'react-router';
import {
  withRouter,
} from 'vibeguide';
import C, { lower } from 'constants';
import PageTitle from './PageTitle';
import RequireAuth from '../RequireAuth/RequireAuth';
import LoginContainer from '../Login/LoginContainer';
import MessagesContainer from '../Messages/MessagesContainer';
import MusicContainer from '../Music/MusicContainer';
import ProgramsContainer from '../Programs/ProgramsContainer';
import AdOpsContainer from '../Advertisers/AdOpsContainer';
import LocationsContainer from '../Locations/LocationsContainer';
import UsersContainer from '../Users/UsersContainer';
import IndividualLocationContainer from '../Locations/IndividualLocation/IndividualLocationContainer';
// Route: /users
import UserList from '../Users/UserList';
import ViewUsersWithPermissions from '../Users/ViewUsersWithPermissions';
import PermissionSetList from '../Users/PermissionSetList';
import IdentityProviderContainer from '../Users/IdentityProviders/IdentityProviderContainer';
// Route: /ad-ops
import AdvertiserList from '../Advertisers/AdvertiserList';
import TargetTagsContainer from '../Advertisers/TargetTags/TargetTagsContainer';
import FlightContainer from '../AdOps/Flights/FlightContainer';
import FlightDetails from '../AdOps/Flights/FlightDetails';
import TargetGroupContainer from '../AdOps/TargetGroups/TargetGroupContainer';
import ScreenResolutionContainer from '../AdOps/ScreenResolutions/ScreenResolutionContainer';
import DeliverySystemContainer from '../AdOps/DeliverySystems/DeliverySystemContainer';
import OrderContainer from '../AdOps/Orders/OrderContainer';
import OrderList from '../AdOps/Orders/OrderList';
import OrderDetails from '../AdOps/Orders/Order/OrderDetails';
import OrderDashboard from '../AdOps/Orders/OrderDashboard/OrderDashboard';
import DefaultOrderTables from '../AdOps/Orders/OrderDashboard/OrderTables/DefaultOrderTables';
import ActiveOrderTables from '../AdOps/Orders/OrderDashboard/OrderTables/ActiveOrderTables';
import PendingOrderTables from '../AdOps/Orders/OrderDashboard/OrderTables/PendingOrderTables';
import CreativeOrderTables from '../AdOps/Orders/OrderDashboard/OrderTables/CreativeOrderTables';
import PausedOrderTables from '../AdOps/Orders/OrderDashboard/OrderTables/PausedOrderTables';
import CompletedOrderTables from '../AdOps/Orders/OrderDashboard/OrderTables/CompletedOrderTables';
import CancelledOrderTables from '../AdOps/Orders/OrderDashboard/OrderTables/CancelledOrderTables';
import OrderSegment from '../AdOps/Orders/OrderSegment/OrderSegment';
import InfoCardsOutlet from '../AdOps/Orders/OrderDashboard/InfoCards/InfoCardsOutlet';
import InfoGraph from '../AdOps/Orders/OrderDashboard/InfoGraph/InfoGraph';
// Route: /companies
import CompaniesList from '../Companies/CompaniesList';
import CompanyCategories from '../Companies/CompanyCategories';
import ZoneManagement from '../Companies/ZoneManagement';
// Route: /programs
import BaselinesContainer from '../Programs/Baselines/BaselinesContainer';
import VisualBaselinesList from '../Programs/Baselines/Visual/VisualBaselinesList';
import EventsContainer from '../Programs/Events/EventsContainer';
// Route: /music
import SongsContainer from '../Music/Songs/SongsContainer';
import PlaylistsContainer from '../Music/Playlists/PlaylistsContainer';
import MixesContainer from '../Music/Mixes/MixesContainer';
import UploadContainer from '../Music/Upload/UploadContainer';
import PublishContainer from '../Music/Publish/PublishContainer';
// Route: /messages
import RequestsContainer from '../Messages/Request/RequestsContainer';
import LibraryContainer from '../Messages/Library/LibraryContainer';
import MessageBlocksContainer from '../Messages/MessageBlocks/MessageBlocksContainer';
import LoopsContainer from '../Messages/Loops/LoopsContainer';
// Route: /locations
import LocationsList from '../Locations/LocationsList';
import ImportLocations from '../Locations/Import/ImportLocations';
// Route: /dashboard
import DashboardAlerts from '../Dashboard/DashboardAlerts';
import DashboardSupport from '../Dashboard/DashboardSupport';
import IntegrationLocationFile from '../Locations/Integration/IntegrationLocationFile';
import DashboardExcluded from '../Dashboard/DashboardExcluded';
// Route: /alerts
import AlertList from '../Dashboard/Alerts/AlertList';
// Route: /location
import ScheduleContainer from '../Locations/IndividualLocation/Schedule/ScheduleContainer';
import AdPrograms from '../Locations/IndividualLocation/AdPrograms/AdPrograms';
import ProfileContainer from '../Locations/IndividualLocation/EditLocation/ProfileContainer';
import Content from '../Locations/IndividualLocation/EditLocation/Content';
import TargetTags from '../Locations/IndividualLocation/EditLocation/TargetTags';
import Telemetry from '../Locations/IndividualLocation/EditLocation/Telemetry';
import CompaniesContainer from '../Companies/CompaniesContainer';
// Route: /partners
import PartnersContainer from '../Partners/PartnersContainer';
import PartnersList from '../Partners/PartnersList';
// Route: /reporting
import ReportingContainer from '../Reporting/ReportingContainer';
import ReportingDashboard from '../Reporting/ReportingDashboard/ReportingDashboard';
import Report from '../Reporting/Report/Report';
// Route: /privacy-policy
import PrivacyPolicy from '../Legal/PrivacyPolicy';
// Route: /terms-of-service
import TermsOfService from '../Legal/TermsOfService';

function getRootPagePrograms(user) {
  if (user.can('baseline.view')) {
    return '/programs/baselines/audio';
  }

  if (user.can('event.view')) {
    return '/programs/events';
  }

  return user.getRootUrl();
}

function getRootPageMessages(user) {
  if (user.can('message.view') || user.can('advertisement.view') || user.can('voice.talent')) {
    return '/messages/requests';
  }

  if (user.can('messagelist.view')) {
    return '/messages/blocks';
  }

  return user.getRootUrl();
}

function getRootPageMessageLibrary(user) {
  if (user.can('message.view') || user.can('voice.talent')) {
    return '/messages/library/regular';
  }

  if (user.can('advertisement.view')) {
    return '/messages/library/ads';
  }

  return user.getRootUrl();
}

function getRootPageMusic(user) {
  if (user.can('playlist.view')) {
    return '/music/playlists';
  }

  if (user.can('mix.view')) {
    return '/music/stations';
  }

  if (user.can('music.view')) {
    return '/music/songs';
  }

  return user.getRootUrl();
}

function getRootPageCompanies(user) {
  if (user.can('company.view')) {
    return '/companies/list';
  }

  if (user.can('company_category.view')) {
    return '/companies/categories';
  }

  if (user.can('company_banner.view')) {
    return '/companies/banners';
  }

  return user.getRootUrl();
}

function getRootPageAdOps(user) {
  if (user.can('order.view')) {
    return '/ad-ops/orders/dashboard';
  }

  if (user.can('flight.view')) {
    return `/ad-ops/${lower(C.PROPOSALS)}`;
  }

  if (user.can('advertiser.view')) {
    return '/ad-ops/advertisers';
  }

  if (user.can('targeting_tag.view')) {
    return '/ad-ops/targeting/triton';
  }

  if (user.can('device_attribute.view')) {
    return '/ad-ops/targeting/vistar';
  }

  if (user.can('screen_resolution.view')) {
    return '/ad-ops/resolutions';
  }

  if (user.can('flight_delivery_system.view')) {
    return '/ad-ops/delivery-systems';
  }

  return user.getRootUrl();
}

function getRootPageUsers(user) {
  if (user.can('user.view')) {
    return '/users/accounts/user';
  }

  if (user.can('integration_user.view')) {
    return '/users/accounts/system';
  }

  if (user.can('partner_user.view')) {
    return '/users/accounts/partner';
  }

  if (user.can('permissionset.view')) {
    return '/users/permissions';
  }

  return user.getRootUrl();
}

function getRootPageUserAccounts(user) {
  if (user.can('user.view')) {
    return '/users/accounts/user';
  }

  if (user.can('integration_user.view')) {
    return '/users/accounts/system';
  }

  if (user.can('partner_user.view')) {
    return '/users/accounts/partner';
  }

  return user.getRootUrl();
}

function getRootPagePartners(user) {
  if (user.can('partner.view')) {
    return '/partners/list';
  }

  return user.getRootUrl();
}

function getRootPageReporting(user) {
  if (user.accountType === 'partner' && user.can('partner.view_report')) {
    return '/reporting/dashboard';
  }

  return user.getRootUrl();
}

function Routes({
  isAuthenticated,
  scrollRef,
  user,
}) {
  useEffect(() => {
    // scroll to the top when the page changes (used for card views and react-perfect-scrollbar content containers)
    scrollRef.scrollTop = 0;
  });

  const rootUrl = user.getRootUrl();

  return (
    <RouterRoutes>
      {/* Locations */}
      <Route
        path="/locations"
        element={(
          <RequireAuth can={['location.view']}>
            <LocationsContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <>
              <PageTitle title="Locations" />
              <LocationsList />
            </>
          )}
        />
        <Route
          path="import"
          element={(
            <RequireAuth can={['admin.system_admin', 'location.create']}>
              <PageTitle title="Import Locations" />
              <ImportLocations />
            </RequireAuth>
          )}
        />
      </Route>

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={(
          <RequireAuth can={['admin.system_admin']}>
            <LocationsContainer />
          </RequireAuth>
        )}
      >
        <Route
          path="alerts"
          element={(
            <RequireAuth can={['alert.view']}>
              <PageTitle title="Alerts Dashboard" />
              <DashboardAlerts />
            </RequireAuth>
          )}
        />
        <Route
          path="support"
          element={(
            <RequireAuth can={['location.view_telemetry']}>
              <PageTitle title="Support Dashboard" />
              <DashboardSupport />
            </RequireAuth>
          )}
        />
        <Route
          path="excluded"
          element={(
            <>
              <PageTitle title="Excluded Locations" />
              <DashboardExcluded />
            </>
          )}
        />
      </Route>

      {/* Alerts */}
      <Route
        path="/alerts"
        element={(
          <RequireAuth can={['admin.system_admin', 'alert.view']}>
            <LocationsContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=":type"
          element={(
            <>
              <PageTitle title="Alerts" />
              <AlertList />
            </>
          )}
        />
      </Route>

      {/* Integration */}
      <Route
        path="/integration"
        element={(
          <RequireAuth can={['admin.system_admin']}>
            <LocationsContainer />
          </RequireAuth>
        )}
      >
        <Route
          path="files"
          element={(
            <RequireAuth can={['location_control_file.view']}>
              <PageTitle title="Integration Location Files" />
              <IntegrationLocationFile />
            </RequireAuth>
          )}
        />
      </Route>

      {/* Individual Location */}
      <Route
        path="/location/:id"
        element={(
          <RequireAuth can={['location.view']}>
            <IndividualLocationContainer />
          </RequireAuth>
        )}
      >
        <Route
          path="schedule"
          element={(
            <>
              <PageTitle title="Location Schedule" />
              <ScheduleContainer />
            </>
          )}
        />
        <Route
          path="profile"
          element={(
            <>
              <PageTitle title="Location Profile" />
              <ProfileContainer />
            </>
          )}
        />
        <Route
          path="profile/:type"
          element={(
            <>
              <PageTitle title="Location Profile" />
              <ProfileContainer />
            </>
          )}
        />
        <Route
          path="assignments"
          element={(
            <>
              <PageTitle title="Location Assignments" />
              <Content />
            </>
          )}
        />
        <Route
          path="assignments/:tab"
          element={(
            <>
              <PageTitle title="Location Assignments" />
              <Content />
            </>
          )}
        />
        <Route
          path="assignments/:tab/:type"
          element={(
            <>
              <PageTitle title="Location Assignments" />
              <Content />
            </>
          )}
        />
        <Route
          path="programs"
          element={(
            <RequireAuth can={['location-ad-program.view']}>
              <PageTitle title="Location Ad Programs" />
              <AdPrograms />
            </RequireAuth>
          )}
        />
        <Route
          path="targeting/:type"
          exact
          element={(
            <RequireAuth
              anyOf={[
                'location.assign_targeting_tag',
                'location.assign_device_attribute',
                'location.assign_order_id',
              ]}
            >
              <PageTitle title="Location Ad Targeting" />
              <TargetTags />
            </RequireAuth>
          )}
        />
        <Route
          path="telemetry"
          element={(
            <RequireAuth can={['admin.system_admin', 'location.view_telemetry']}>
              <PageTitle title="Location Telemetry" />
              <Telemetry />
            </RequireAuth>
          )}
        >
          <Route
            path=":type"
            element={(
              <>
                <PageTitle title="Location Telemetry" />
                <Telemetry />
              </>
            )}
          />
        </Route>
      </Route>

      {/* Messages (Requests, Library, Message Blocks) */}
      <Route
        path="/messages"
        element={(
          <RequireAuth anyOf={['message.view', 'advertisement.view', 'messagelist.view', 'voice.talent']}>
            <MessagesContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPageMessages(user)}
            />
          )}
        />
        <Route
          path="requests"
          element={(
            <RequireAuth anyOf={['message.view', 'advertisement.view', 'voice.talent']}>
              <PageTitle title="Message Requests" />
              <RequestsContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="requests/:type"
          element={(
            <RequireAuth anyOf={['message.view', 'advertisement.view', 'voice.talent']}>
              <PageTitle title="Message Requests" />
              <RequestsContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="library"
          element={(
            <Navigate
              to={getRootPageMessageLibrary(user)}
            />
          )}
        />
        <Route
          path="library/:type"
          element={(
            <RequireAuth anyOf={['message.view', 'advertisement.view']}>
              <PageTitle title="Message Library" />
              <LibraryContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="blocks"
          element={(
            <RequireAuth can={['messagelist.view']}>
              <PageTitle title="Message Blocks" />
              <MessageBlocksContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="loops"
          element={(
            <RequireAuth can={['loop.view']}>
              <PageTitle title="Loops" />
              <LoopsContainer />
            </RequireAuth>
          )}
        />
        {/* <Route
          path="ad/library"
          element={(
            <RequireAuth can={['message.view', 'advertisement.view']}>
              <LibraryContainer />
            </RequireAuth>
          )}
        /> */}
      </Route>

      {/* Music (Playlists, Stations, Songs) */}
      <Route
        path="/music"
        element={(
          <RequireAuth anyOf={['playlist.view', 'mix.view', 'music.view']}>
            <MusicContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPageMusic(user)}
            />
          )}
        />
        <Route
          path="playlists"
          element={(
            <RequireAuth can={['playlist.view']}>
              <PageTitle title="Playlists" />
              <PlaylistsContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="stations"
          element={(
            <RequireAuth can={['mix.view']}>
              <PageTitle title="Stations" />
              <MixesContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="songs"
          element={(
            <RequireAuth can={['music.view']}>
              <PageTitle title="Songs" />
              <SongsContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="songs/upload"
          element={(
            <RequireAuth can={['music.create']}>
              <PageTitle title="Upload Songs" />
              <UploadContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="songs/publish"
          element={(
            <RequireAuth can={['music.create']}>
              <PageTitle title="Publish Songs" />
              <PublishContainer />
            </RequireAuth>
          )}
        />
      </Route>

      {/* Programs (Baselines, Events) */}
      <Route
        path="/programs"
        element={(
          <RequireAuth anyOf={['baseline.view', 'event.view']}>
            <ProgramsContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPagePrograms(user)}
            />
          )}
        />
        <Route
          path="baselines/audio"
          element={(
            <RequireAuth can={['baseline.view']}>
              <PageTitle title="Audio Baselines" />
              <BaselinesContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="baselines/visual"
          element={(
            <RequireAuth can={['baseline.view']}>
              <PageTitle title="Visual Baselines" />
              <VisualBaselinesList />
            </RequireAuth>
          )}
        />
        <Route
          path="events"
          element={(
            <RequireAuth can={['event.view']}>
              <PageTitle title="Events" />
              <EventsContainer
                scrollRef={scrollRef}
              />
            </RequireAuth>
          )}
        />
      </Route>

      {/* Companies */}
      <Route
        path="/companies"
        element={(
          <RequireAuth anyOf={['company.view', 'company_category.view', 'company_banner.view']}>
            <CompaniesContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPageCompanies(user)}
            />
          )}
        />
        <Route
          path="list"
          element={(
            <RequireAuth can={['company.view']}>
              <PageTitle title="Companies" />
              <CompaniesList />
            </RequireAuth>
          )}
        />
        <Route
          path="categories"
          element={(
            <RequireAuth can={['company_category.view']}>
              <PageTitle title="Categories" />
              <CompanyCategories />
            </RequireAuth>
          )}
        />
        <Route
          path="zones"
          element={(
            <RequireAuth can={['location-zone.view']}>
              <PageTitle title="Zones" />
              <ZoneManagement />
            </RequireAuth>
          )}
        />
      </Route>

      {/* Ad Ops */}
      <Route
        path="/ad-ops"
        element={(
          <RequireAuth
            anyOf={[
              'flight.view',
              'order.view',
              'advertiser.view',
              'targeting_tag.view',
              'device_attribute.view',
              'target_group.view',
              'screen_resolution.view',
              'flight_delivery_system.view',
            ]}
          >
            <AdOpsContainer
              scrollRef={scrollRef}
            />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPageAdOps(user)}
            />
          )}
        />
        <Route
          path="advertisers"
          element={(
            <RequireAuth can={['advertiser.view']}>
              <PageTitle title="Advertisers" />
              <AdvertiserList
                scrollRef={scrollRef}
              />
            </RequireAuth>
          )}
        />
        <Route
          path="targeting/:type"
          element={(
            <RequireAuth anyOf={['targeting_tag.view', 'device_attribute.view']}>
              <PageTitle title="Ad Targeting" />
              <TargetTagsContainer />
            </RequireAuth>
          )}
        />

        <Route
          path="orders"
          element={(
            <RequireAuth can={['order.view']}>
              <OrderContainer />
            </RequireAuth>
          )}
        >
          <Route
            path="dashboard"
            element={<OrderDashboard />}
          >
            <Route
              index
              element={(
                <>
                  <PageTitle title="Order Dashboard" />
                  <DefaultOrderTables />
                </>
              )}
            />
            <Route
              path="live"
              element={(
                <>
                  <PageTitle title="Order Dashboard - Live" />
                  <ActiveOrderTables />
                </>
              )}
            />
            <Route
              path="pending"
              element={(
                <>
                  <PageTitle title="Order Dashboard - Pending" />
                  <PendingOrderTables />
                </>
              )}
            />
            <Route
              path="awaitingcreative"
              element={(
                <>
                  <PageTitle title="Order Dashboard - Awaiting Creative" />
                  <CreativeOrderTables />
                </>
              )}
            />
            <Route
              path="paused"
              element={(
                <>
                  <PageTitle title="Order Dashboard - Paused" />
                  <PausedOrderTables />
                </>
              )}
            />
            <Route
              path="completed"
              element={(
                <>
                  <PageTitle title="Order Dashboard - Completed" />
                  <CompletedOrderTables />
                </>
              )}
            />
            <Route
              path="cancelled"
              element={(
                <>
                  <PageTitle title="Order Dashboard - Cancelled" />
                  <CancelledOrderTables />
                </>
              )}
            />
          </Route>

          <Route path=":status">
            <Route
              index
              element={(
                <>
                  <PageTitle title="Orders" />
                  <OrderList />
                </>
              )}
            />
            <Route
              path=":orderType"
              element={(
                <>
                  <PageTitle title="Orders" />
                  <OrderList />
                </>
              )}
            />
          </Route>
        </Route>

        {/* Individual Order */}
        <Route
          path="order/:id"
          element={(
            <RequireAuth can={['order.view']}>
              <OrderContainer />
            </RequireAuth>
          )}
        >
          <Route
            path="details"
            element={(
              <>
                <PageTitle title="Order Details" />
                <OrderDetails />
              </>
            )}
          >
            <Route
              index
              element={(
                <InfoCardsOutlet />
              )}
            />

            <Route
              path="graph"
              element={(
                <InfoGraph />
              )}
            />
          </Route>

          <Route
            path="segment/:segmentId/:type?"
            element={(
              <>
                <PageTitle title="Order Segment" />
                <OrderSegment />
              </>
            )}
          />

          <Route
            path=":type"
            element={(
              <>
                <PageTitle title="Order Details" />
                <OrderDetails />
              </>
            )}
          >
            <Route
              index
              element={(
                <InfoCardsOutlet />
              )}
            />

            <Route
              path="graph"
              element={(
                <InfoGraph />
              )}
            />
          </Route>
        </Route>

        <Route
          path={`${lower(C.PROPOSALS)}/:type?`}
          element={(
            <RequireAuth can={['flight.view']}>
              <PageTitle title={C.PROPOSALS} />
              <FlightContainer />
            </RequireAuth>
          )}
        />
        <Route
          path={`${lower(C.PROPOSAL)}/:id`}
          element={(
            <RequireAuth can={['flight.view']}>
              <PageTitle title={`${C.PROPOSAL} Details`} />
              <FlightDetails />
            </RequireAuth>
          )}
        />
        <Route
          path={`${lower(C.PROPOSAL)}/:id/:type`}
          element={(
            <RequireAuth can={['flight.view']}>
              <PageTitle title={`${C.PROPOSAL} Details`} />
              <FlightDetails />
            </RequireAuth>
          )}
        />
        <Route
          path="target-groups"
          element={(
            <RequireAuth can={['target_group.view']}>
              <PageTitle title="Target Groups" />
              <TargetGroupContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="resolutions"
          element={(
            <RequireAuth can={['screen_resolution.view']}>
              <PageTitle title="Screen Resolutions" />
              <ScreenResolutionContainer />
            </RequireAuth>
          )}
        />
        <Route
          path="delivery-systems"
          element={(
            <RequireAuth can={['flight_delivery_system.view']}>
              <PageTitle title="Delivery Systems" />
              <DeliverySystemContainer />
            </RequireAuth>
          )}
        />
      </Route>

      {/* Users */}
      <Route
        path="/users"
        element={(
          <RequireAuth
            anyOf={[
              'user.view',
              'integration_user.view',
              'partner_user.view',
              'permissionset.view',
              'identity_provider.view',
            ]}
          >
            <UsersContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPageUsers(user)}
            />
          )}
        />
        <Route
          path="accounts"
          element={(
            <Navigate
              to={getRootPageUserAccounts(user)}
            />
          )}
        />
        <Route
          path="accounts/:type"
          element={(
            <RequireAuth
              anyOf={[
                'user.view',
                'integration_user.view',
                'partner_user.view',
              ]}
            >
              <PageTitle title="Users" />
              <UserList />
            </RequireAuth>
          )}
        />
        <Route
          path="view-users-with-permissions"
          element={(
            <RequireAuth
              can={['admin.system_admin']}
              anyOf={[
                'user.view',
                'integration_user.view',
                'partner_user.view',
              ]}
            >
              <PageTitle title="View Users with Permissions" />
              <ViewUsersWithPermissions />
            </RequireAuth>
          )}
        />
        <Route
          path="permissions"
          element={(
            <RequireAuth can={['permissionset.view']}>
              <PageTitle title="Permission Sets" />
              <PermissionSetList />
            </RequireAuth>
          )}
        />
        <Route
          path="identity-providers"
          element={(
            <RequireAuth can={['identity_provider.view']}>
              <PageTitle title="Identity Providers" />
              <IdentityProviderContainer />
            </RequireAuth>
          )}
        />
      </Route>

      {/* Partners */}
      <Route
        path="/partners"
        element={(
          <RequireAuth anyOf={['partner.view']}>
            <PartnersContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPagePartners(user)}
            />
          )}
        />
        <Route
          path="list"
          element={(
            <>
              <PageTitle title="Partners" />
              <PartnersList />
            </>
          )}
        />
      </Route>

      {/* Reporting */}
      <Route
        path="/reporting"
        element={(
          <RequireAuth anyOf={['partner.view_report']}>
            <ReportingContainer />
          </RequireAuth>
        )}
      >
        <Route
          path=""
          element={(
            <Navigate
              to={getRootPageReporting(user)}
            />
          )}
        />
        <Route
          path="dashboard"
          element={(
            <>
              <PageTitle title="Reporting Dashboard" />
              <ReportingDashboard />
            </>
          )}
        />
        <Route
          path="report/:reportName"
          element={(
            <>
              <PageTitle title="Report" />
              <Report />
            </>
          )}
        />
      </Route>

      {/* Privacy Policy */}
      <Route
        path="/privacy-policy"
        element={(
          <>
            <PageTitle title="Privacy Policy" />
            <PrivacyPolicy />
          </>
        )}
      />

      {/* Terms of Service */}
      <Route
        path="/terms-of-service"
        element={(
          <>
            <PageTitle title="Terms of Service" />
            <TermsOfService />
          </>
        )}
      />

      <Route
        path="/login"
        element={(
          <>
            <PageTitle title="In-Store Marketplace" />
            <LoginContainer
              path="/login"
            />
          </>
        )}
      />

      <Route
        path="/"
        element={(
          <>
            <PageTitle title="In-Store Marketplace" />
            <LoginContainer
              path="/login"
            />
          </>
        )}
      />

      {!isAuthenticated && (
        <>
          <Route
            path="/forgot"
            element={(
              <>
                <PageTitle title="Forgot Password" />
                <LoginContainer
                  path="/forgot"
                />
              </>
            )}
          />

          <Route
            path="/reset"
            element={(
              <>
                <PageTitle title="Reset Password" />
                <LoginContainer
                  path="/reset"
                />
              </>
            )}
          />
        </>
      )}

    </RouterRoutes>
  );
}

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default withRouter(connect(mapStateToProps)(Routes));
